import {Injectable} from "@angular/core";
import {Actions, createEffect, ofType} from "@ngrx/effects";
import {concatLatestFrom} from "@ngrx/operators";
import {Store} from "@ngrx/store";
import * as AccountActions from './account.actions';
import {selectAccountState} from './account.selectors';
import {AccountService} from "@core/services/account.service";
import {exhaustMap, filter, mergeMap, of, switchMap, tap} from "rxjs";
import {catchError, delay, map, pairwise} from "rxjs/operators";
import {Router} from '@angular/router';
import {extractErrorMessage} from "@core/utils/error-message.util";
import * as AuthActions from '../auth/auth.actions';
import {SCHOOL_ACCOUNT_CLOSED_MESSAGE} from '@core/services/auth.service';
import {MatSnackBar} from '@angular/material/snack-bar';

// How long a successful load is considered fresh before loadUsersIfNeeded refetches.
const USERS_CACHE_TTL_MS = 60_000;

// How often the page asks the backend how far a running synchronization has got. A sync takes
// minutes, so this is about keeping the page honest, not about being quick.
const SYNC_STATUS_POLL_INTERVAL_MS = 5_000;

@Injectable()
export class AccountEffects {
    constructor(private actions$: Actions,
        private accountService: AccountService,
        private store: Store,
        private router: Router,
        private snackBar: MatSnackBar) {}

    // Store-first guard: skip the fetch when a load is already in flight or the cache
    // is fresh, so pages can dispatch loadUsersIfNeeded on every entry and render the
    // cached roster instantly.
    loadUsersIfNeeded$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AccountActions.loadUsersIfNeeded),
            concatLatestFrom(() => this.store.select(selectAccountState)),
            filter(([, state]) => !state.userLoading
                && (!state.usersLoaded || Date.now() - (state.usersLastLoadedAt ?? 0) > USERS_CACHE_TTL_MS)),
            map(() => AccountActions.loadUsers())
        )
    );

    loadUsers$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AccountActions.loadUsers),
            switchMap(() =>
                this.accountService.loadUsers().pipe(
                    map(users => AccountActions.loadUsersSuccess({ users })),
                    catchError(error => of(AccountActions.loadUsersFailure({
                        error: extractErrorMessage(error)
                    })))
                )
            )
        )
    );

    addUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AccountActions.addUser),
            mergeMap(({ request }) =>
                this.accountService.addUser(request).pipe(
                    map(created => AccountActions.addUserSuccess({ user: created })),
                    catchError(error => {
                        console.error('Add user effect error:', error);
                        return of(AccountActions.addUserFailure({
                            error: extractErrorMessage(error)
                        }));
                    })
                )
            )
        )
    );

    addUserSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AccountActions.addUserSuccess),
            tap(({ user }) => {
                // Delay navigation to allow snackbar to be visible
                setTimeout(() => this.router.navigate(["/people/users"]), 1500);
            })
        ), { dispatch: false }
    );

    updateUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AccountActions.updateUser),
            mergeMap(({ request }) =>
                this.accountService.updateUser(request).pipe(
                    map(updated => AccountActions.updateUserSuccess({ user: updated })),
                    catchError(error => {
                        console.error('Update user effect error:', error);
                        return of(AccountActions.updateUserFailure({
                            error: extractErrorMessage(error)
                        }));
                    })
                )
            )
        )
    );

    removeUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AccountActions.removeUser),
            mergeMap(({ userAccountId }) =>
                this.accountService.removeUser(userAccountId).pipe(
                    map(() => AccountActions.removeUserSuccess({ userAccountId })),
                    catchError(error => of(AccountActions.removeUserFailure({
                        error: extractErrorMessage(error)
                    })))
                )
            )
        )
    );

    updateUserProfile$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AccountActions.updateUserProfile),
            mergeMap(({ request }) =>
                this.accountService.updateUserProfile(request).pipe(
                    map(updated => AccountActions.updateUserProfileSuccess({ userProfile: updated })),
                    catchError(error => of(AccountActions.updateUserProfileFailure({
                        error: extractErrorMessage(error)
                    })))
                )
            )
        )
    );

    loadSchoolInfo$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AccountActions.loadSchoolInfo),
            mergeMap(({ schoolAccountId }) =>
                this.accountService.loadSchoolInfo(schoolAccountId).pipe(
                    map(schoolInfo => AccountActions.loadSchoolInfoSuccess({ schoolInfo })),
                    catchError(error => of(AccountActions.loadSchoolInfoFailure({
                        error: extractErrorMessage(error)
                    })))
                )
            )
        )
    );

    syncSchoolDataFromIntegration$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AccountActions.syncSchoolDataFromIntegration),
            mergeMap(({ schoolId, schoolAccountId, confirmLargeRemovals, syncMode }) =>
                this.accountService.syncSchoolAndStudentsFromIntegration(schoolId, schoolAccountId, !!confirmLargeRemovals, syncMode).pipe(
                    map(() => AccountActions.syncSchoolDataFromIntegrationStarted({ schoolAccountId })),
                    catchError(error => {
                        console.error('Sync school and students integration error:', error);
                        return of(AccountActions.syncSchoolDataFromIntegrationFailure({
                            error: extractErrorMessage(error)
                        }));
                    })
                )
            )
        )
    );

    setSyncMode$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AccountActions.setSyncMode),
            switchMap(({ schoolAccountId, syncMode }) =>
                this.accountService.setSyncMode(schoolAccountId, syncMode).pipe(
                    map(saved => AccountActions.setSyncModeSuccess({ syncMode: saved })),
                    catchError(error => of(AccountActions.setSyncModeFailure({
                        error: extractErrorMessage(error)
                    })))
                )
            )
        )
    );

    /** The run is on the backend now, so the page follows it through the status endpoint. */
    followStartedSync$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AccountActions.syncSchoolDataFromIntegrationStarted),
            map(({ schoolAccountId }) => AccountActions.loadSyncStatus({ schoolAccountId }))
        )
    );

    loadSyncStatus$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AccountActions.loadSyncStatus),
            switchMap(({ schoolAccountId }) =>
                this.accountService.getSyncStatus(schoolAccountId).pipe(
                    map(status => AccountActions.loadSyncStatusSuccess({ schoolAccountId, status })),
                    catchError(error => of(AccountActions.loadSyncStatusFailure({
                        error: extractErrorMessage(error)
                    })))
                )
            )
        )
    );

    /**
     * Keeps asking while a run is in progress, and stops on the answer that says it finished. The
     * polling lives in the store rather than the page, so an admin who navigates away and comes
     * back finds the run still being followed.
     */
    syncRunFinished$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AccountActions.loadSyncStatusSuccess),
            pairwise(),
            filter(([previous, current]) => previous.status.running && !current.status.running),
            map(([, current]) => AccountActions.syncRunFinished({ schoolAccountId: current.schoolAccountId }))
        )
    );

    pollSyncStatus$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AccountActions.loadSyncStatusSuccess),
            filter(({ status }) => status.running),
            delay(SYNC_STATUS_POLL_INTERVAL_MS),
            map(({ schoolAccountId }) => AccountActions.loadSyncStatus({ schoolAccountId }))
        )
    );

    updateSchool$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AccountActions.updateSchool),
            mergeMap(({ request }) =>
                this.accountService.updateSchool(request).pipe(
                    map(name => AccountActions.updateSchoolSuccess({ name })),
                    catchError(error => of(AccountActions.updateSchoolFailure({
                        error: extractErrorMessage(error)
                    })))
                )
            )
        )
    );

    // DIGI-1855: once the school is closed the admin's own session is over, so sign out.
    deactivateSchool$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AccountActions.deactivateSchool),
            // One request at a time: a second click while the first is in flight is dropped (final review, L3).
            exhaustMap(({ confirmSchoolName }) =>
                this.accountService.deactivateSchool(confirmSchoolName).pipe(
                    map(() => AccountActions.deactivateSchoolSuccess()),
                    catchError(error => {
                        const message = extractErrorMessage(error);
                        // Already closed (a retry, a second tab or click): the school is gone, so
                        // this is the same outcome as a success and the admin is signed out.
                        return of(message === SCHOOL_ACCOUNT_CLOSED_MESSAGE
                            ? AccountActions.deactivateSchoolSuccess()
                            : AccountActions.deactivateSchoolFailure({ error: message }));
                    })
                )
            )
        )
    );

    deactivateSchoolSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AccountActions.deactivateSchoolSuccess),
            // The snack bar outlives the move to the login page, so the admin sees it worked.
            tap(() => this.snackBar.open('Your school account has been deactivated.', 'OK', { duration: 10000 })),
            map(() => AuthActions.logout())
        )
    );

    updateSchoolLogo$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AccountActions.updateSchoolLogo),
            mergeMap(({ request }) =>
                this.accountService.updateSchoolLogo(request).pipe(
                    map(logo => AccountActions.updateSchoolLogoSuccess({ logo })),
                    catchError(error => of(AccountActions.updateSchoolLogoFailure({
                        error: extractErrorMessage(error)
                    })))
                )
            )
        )
    );

    updateSchoolContact$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AccountActions.updateSchoolContact),
            mergeMap(({ request }) =>
                this.accountService.updateSchoolContact(request).pipe(
                    map(applied => {
                        const mappedAddress = applied.address;
                        return AccountActions.updateSchoolContactSuccess({
                            phoneNumbers: applied.phoneNumbers,
                            notificationRecipientIds: applied.notificationRecipientIds,
                            address: mappedAddress ? {
                                line1: mappedAddress.line1,
                                line2: mappedAddress.line2,
                                town: mappedAddress.town,
                                postCode: mappedAddress.postCode,
                                countryCode: '',
                                countryName: mappedAddress.country,
                                countryIso31662: '',
                                creationDatetime: new Date(),
                                lastModifiedDatetime: new Date()
                            } : undefined
                        });
                    }),
                    catchError(error => of(AccountActions.updateSchoolContactFailure({
                        error: extractErrorMessage(error)
                    })))
                )
            )
        )
    );

}